// src/pages/providers/2_dashboard/components/PatientSetupForm.js
import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db, auth } from '../../../../firebase';
import Select from 'react-select';
import { GlobalStateContext } from '../../../../context/GlobalStateContext';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/solid';
import { functions } from '../../../../firebase';
import { httpsCallable } from 'firebase/functions';
import { getConfigPatientSetup, demoIdentityForCypPreset } from '../config/patientSetupConfig';
import DateOfBirthPicker from '../../../serviceUsers/3_forms/components/DateOfBirthPicker';
import { toast } from 'react-toastify';
import { sanitizeNameInput, sanitizeEmailInput } from '../../../../utils/inputSanitization';
import { buildOptimisticPatientSummary } from '../../../../utils/buildOptimisticPatientSummary';
import { canClinicianBeAssignedToPathway } from '../../../../utils/pathwaySpecialismUtils';
import { validateEmail, getStepValidation, getMinSelections, isFieldEnabled, applyDependentFieldClears } from '../utils/patientSetupValidation';
import getScrollablePortalTarget from '../../../../utils/getScrollablePortalTarget';
import substringFilterOption from '../utils/substringFilterOption';

// Get environment from environment variables, defaulting to 'production' if not set
const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT || process.env.NODE_ENV || 'production';
const IS_PRODUCTION = ENVIRONMENT === 'production';

// Log environment info in non-production environments
if (!IS_PRODUCTION) {
    console.log(`Patient Setup running in ${ENVIRONMENT} environment - Demo/Test options will be shown`);
}

// Human-readable labels for assessment module values.
// Used in the setup form's module selector step.
const ASSESSMENT_MODULE_LABELS = {
    adultAdhd: 'Adult ADHD',
    adultAutism: 'Adult Autism',
    cypAdhd: 'CYP ADHD',
};

/**
 * Shared react-select props for every dropdown rendered inside this dialog.
 *
 * Portals the menu to a scroll-safe container on <body> (see
 * getScrollablePortalTarget) with fixed positioning so it escapes the
 * dialog's CSS transform and overflow clipping while remaining scrollable
 * despite Radix's react-remove-scroll lock.
 */
const PATIENT_SETUP_SELECT_MENU_PROPS = {
    classNamePrefix: 'td-select',
    menuPortalTarget: getScrollablePortalTarget(),
    menuPosition: 'fixed',
    isSearchable: true,
    filterOption: substringFilterOption,
    styles: {
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    },
};

function PatientSetupForm({ isOpen, onClose }) {
    const { userData, refreshUserDataOnly, optimisticAddPatient } = useContext(GlobalStateContext);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    // Update the state to store different types of user options
    const [clinicianOptions, setClinicianOptions] = useState([]); // For Lead/Additional Clinicians

    const [currentStep, setCurrentStep] = useState(1);

    // Assessment module selection: when the org offers >1 module, the user picks one
    // before entering patient details.  Fetched from getOrgConfig on first open, then
    // cached in a ref so all subsequent opens are instant (no loading spinner or flicker).
    // We never show a loading spinner — single-module orgs render the form instantly,
    // and multi-module orgs upgrade to show the selector once the fetch resolves.
    const modulesCache = useRef(null);
    const [availableModules, setAvailableModules] = useState(() => {
        return modulesCache.current || ['adultAdhd'];
    });
    const [selectedModule, setSelectedModule] = useState('adultAdhd');
    const [modulesLoading, setModulesLoading] = useState(false);
    const hasMultipleModules = availableModules.length > 1;

    /**
     * Freeze the "organisation config" for the lifetime of an open dialog session.
     *
     * Why:
     * - After submitting, we call `refreshUserDataOnly()`, which updates `userData` in GlobalStateContext.
     * - `orgConfig` is derived from `userData.organisation`, so React recreates it on refresh.
     * - Our current init effect resets the form any time `orgConfig` changes while the dialog is open,
     *   which looks like the user is "kicked back to step 1 with an empty form" before the dialog closes.
     *
     * Fix:
     * - Capture the config ON OPEN and keep using that stable copy until the dialog closes.
     * - This prevents unrelated background refreshes from wiping the in-progress UI.
     */
    const [sessionOrgConfig, setSessionOrgConfig] = useState(null);
    const buildOrgConfig = useCallback((organisationName, userType, module) => {
        const config = getConfigPatientSetup(organisationName, module);

        /**
         * Org Admin only UX: "Auto-create informants?"
         *
         * Business context:
         * - We support creating demo/training patients in non-production environments.
         * - Demo/training patients are most useful when they also have informant data prefilled.
         * - Today, informants must be manually created by logging in as the patient and completing
         *   the informant setup form, which is time-consuming.
         *
         * Design decisions:
         * - This toggle is operational (how the system should behave at creation time), NOT patient data.
         *   So it must NOT live in `customFields` and should not be persisted as part of patient metadata.
         * - It is only relevant for Org Admins (we don't want to change clinician/support workflows).
         * - We inject the field into the same "Patient Type" step (the step that contains `demoOrTest`)
         *   so we don't need to duplicate config changes across org-specific step numbers (5/7/10 etc.).
         * - Default for Org Admins is "Auto-Create Informants" to reduce friction for demos/training.
         */
        const isOrgAdmin = userType === 'Org. Admin';
        const injectAutoCreateInformantsField = (baseConfig, module) => {
            if (!isOrgAdmin) return baseConfig;
            if (!baseConfig?.steps) return baseConfig;

            // Find the step that contains the demo/test dropdown (varName: demoOrTest)
            const steps = baseConfig.steps;
            for (const stepKey of Object.keys(steps)) {
                const step = steps[stepKey];
                if (!step?.fields) continue;

                const hasDemoOrTest = Object.values(step.fields).some(
                    (f) => f?.varName === 'demoOrTest'
                );
                if (!hasDemoOrTest) continue;

                // Pick the next numeric field key (fields are keyed as strings: "1", "2", ...)
                const existingKeys = Object.keys(step.fields)
                    .map((k) => parseInt(k, 10))
                    .filter((n) => Number.isFinite(n));
                const nextKey = (Math.max(0, ...existingKeys) + 1).toString();

                // Autism pathway: offer granular "1 or 2 informants" options instead of a boolean toggle,
                // since autism patients can have up to 2 non-life-stage informants answering identical forms.
                const isAutism = module === 'adultAutism';
                // CYP ADHD: auto-create means pre-nominating a school representative with completed
                // forms; without it the parent's school-nomination journey stays testable.
                const isCypAdhd = module === 'cypAdhd';
                const options = isAutism
                    ? [
                        { value: "autoCreateOneInformant", label: "Auto-Create One Informant" },
                        { value: "autoCreateTwoInformants", label: "Auto-Create Two Informants" },
                        { value: false, label: "Don't Auto-Create Informants" },
                    ]
                    : isCypAdhd
                        ? [
                            { value: true, label: "Auto-Create School Informant" },
                            { value: false, label: "Don't Auto-Create (test school nomination)" },
                        ]
                        : [
                            { value: true, label: "Auto-Create Informants" },
                            { value: false, label: "Don't Auto-Create Informants" },
                        ];

                // Value is stored as a one-item array so it matches the other
                // selectOneDropdown fields in this form.
                step.fields[nextKey] = {
                    varName: 'autoCreateInformantsRequested',
                    displayText: "Auto-create informants?",
                    inputType: 'selectOneDropdown',
                    options,
                    required: false,
                    // Default to the first "create" option for Org Admins only.
                    defaultValue: [options[0]],
                };

                return baseConfig; // Only inject once
            }

            return baseConfig;
        };

        // If we're in production, filter out the "Patient Type" step
        if (IS_PRODUCTION && config && config.steps) {
            // Create a deep copy of the config to avoid mutating the original
            const filteredConfig = JSON.parse(JSON.stringify(config));

            // Find and remove the "Patient Type" step
            Object.keys(filteredConfig.steps).forEach(stepKey => {
                const step = filteredConfig.steps[stepKey];
                if (step.fields &&
                    Object.values(step.fields).some(field => field.varName === 'demoOrTest')) {
                    delete filteredConfig.steps[stepKey];
                }
            });

            // Renumber the steps to ensure sequential numbering
            const newSteps = {};
            let newIndex = 1;

            Object.keys(filteredConfig.steps)
                .sort((a, b) => parseInt(a) - parseInt(b))
                .forEach(oldKey => {
                    newSteps[newIndex.toString()] = filteredConfig.steps[oldKey];
                    newIndex++;
                });

            filteredConfig.steps = newSteps;
            // In production, the patient type step is removed, so there is no place to inject.
            return filteredConfig;
        }

        // Non-production: clone and inject Org Admin-only field (if the demo/test step exists)
        const clonedConfig = config ? JSON.parse(JSON.stringify(config)) : config;
        return injectAutoCreateInformantsField(clonedConfig, module);
    }, []);

    // The config the form should use right now:
    // Rebuilt whenever the selected module changes so that pathway-specific
    // overrides (e.g. restricted contract types for adultAutism) apply
    // dynamically after the user picks a pathway on step 1.
    const orgConfig = useMemo(() => {
        return buildOrgConfig(userData?.organisation, userData?.userType, selectedModule);
    }, [buildOrgConfig, userData?.organisation, userData?.userType, selectedModule]);

    // Get total steps from config.  When the org offers multiple assessment modules,
    // we prepend a "module selector" step (step 1) before the standard config steps.
    const totalSteps = useMemo(() => {
        const configSteps = Object.keys(orgConfig?.steps || {}).length;
        return hasMultipleModules ? configSteps + 1 : configSteps;
    }, [orgConfig, hasMultipleModules]);

    // The step index into orgConfig.steps is offset by 1 when the module selector is shown.
    // This maps the UI's currentStep to the config step key.
    const configStepOffset = hasMultipleModules ? 1 : 0;

    // Initialize form data structure
    const [formData, setFormData] = useState({});

    // Track open transitions so we only initialize ONCE per open session.
    const wasOpenRef = useRef(false);

    // Animated height transition when switching wizard steps (matches ManageOrgConfigDialog).
    const contentRef = useRef(null);
    const [animatedHeight, setAnimatedHeight] = useState('auto');

    // Re-measure when step content changes; ResizeObserver catches late layout
    // shifts (fonts, borders on selected pathway buttons, etc.).
    useEffect(() => {
        if (!isOpen || !contentRef.current) return undefined;

        const el = contentRef.current;
        const updateHeight = () => {
            setAnimatedHeight(`${el.scrollHeight}px`);
        };

        updateHeight();
        const timer = setTimeout(updateHeight, 30);

        const resizeObserver = new ResizeObserver(updateHeight);
        resizeObserver.observe(el);

        return () => {
            clearTimeout(timer);
            resizeObserver.disconnect();
        };
    }, [isOpen, currentStep, modulesLoading, hasMultipleModules, submitError, selectedModule]);

    // Capture a stable orgConfig when the dialog opens, and clear when it closes.
    useEffect(() => {
        const wasOpen = wasOpenRef.current;
        wasOpenRef.current = isOpen;

        if (isOpen && !wasOpen) {
            // Dialog just opened → capture config for this session.
            // selectedModule at this point is the cached default; once the user
            // picks a pathway the orgConfig memo will re-derive with the new module.
            setSessionOrgConfig(buildOrgConfig(userData?.organisation, userData?.userType, selectedModule));
            return;
        }
        if (!isOpen && wasOpen) {
            // Dialog just closed → release the session config and reset state.
            // Use cached modules (if available) so next open is instant.
            setSessionOrgConfig(null);
            setCurrentStep(1);
            setSubmitError('');
            setIsSubmitting(false);
            setFormData({ customFields: {} });
            setAvailableModules(modulesCache.current || ['adultAdhd']);
            setSelectedModule((modulesCache.current || ['adultAdhd'])[0]);
            setModulesLoading(false);
            setAnimatedHeight('auto');
        }
    }, [isOpen, buildOrgConfig, userData?.organisation, userData?.userType, selectedModule]);

    // Fetch the org's availableAssessmentModules via getOrgConfig on first open.
    // Results are cached in modulesCache so subsequent opens resolve instantly
    // without a loading state or network call. The cache lives for the component's
    // lifetime (i.e. until the user navigates away from the dashboard).
    //
    // Optimisation: we show the form content immediately (no loading spinner) and
    // only upgrade to multi-module mode if the fetch reveals multiple modules.
    // For single-module orgs (the majority), this means zero delay — the patient
    // details form renders instantly on every open.
    useEffect(() => {
        if (!isOpen) return;

        // Already fetched in a previous open — apply from cache instantly
        if (modulesCache.current) {
            setAvailableModules(modulesCache.current);
            setSelectedModule(modulesCache.current[0] || 'adultAdhd');
            setModulesLoading(false);
            return;
        }

        // Show the form immediately (defaulting to single-module/ADHD layout).
        // If the org turns out to have multiple modules, the fetch below will
        // upgrade the UI to show the module selector. This eliminates the
        // "null state" loading spinner entirely for single-module orgs.
        setModulesLoading(false);

        let cancelled = false;
        const fetchModules = async () => {
            try {
                const getOrgConfigFn = httpsCallable(functions, 'getOrgConfig');
                const result = await getOrgConfigFn();
                if (cancelled) return;
                const modules = result?.data?.availableAssessmentModules;
                if (Array.isArray(modules) && modules.length > 0) {
                    modulesCache.current = modules;
                    setAvailableModules(modules);
                    setSelectedModule(modules[0]);
                }
            } catch (err) {
                console.warn('Failed to fetch availableAssessmentModules:', err);
            }
        };
        fetchModules();
        return () => { cancelled = true; };
    }, [isOpen]);

    // Initialize form data structure ONCE per open, using the captured session config.
    useEffect(() => {
        // Only initialize when opening AND after the session config is ready.
        if (!isOpen || !sessionOrgConfig) return;

        setCurrentStep(1);
        setSubmitError('');

        // Initialize form data
        const initialData = {
            customFields: {}
        };

        // Check if orgConfig and steps exist before processing
        if (sessionOrgConfig && sessionOrgConfig.steps) {
            // Initialize fields from all steps
            Object.keys(sessionOrgConfig.steps).forEach(stepKey => {
                const step = sessionOrgConfig.steps[stepKey];

                // Check if step and fields exist
                if (step && step.fields) {
                    Object.keys(step.fields).forEach(fieldKey => {
                        const field = step.fields[fieldKey];
                        if (field && field.varName) {
                            // Initialize fields based on their input type
                            let initialValue = '';

                            // For button selector fields, use appropriate empty values
                            if (field.inputType === 'selectOneButtons') {
                                initialValue = null;
                            } else if (field.inputType === 'selectMultipleButtons') {
                                initialValue = [];
                            } else if (field.inputType === 'selectOneDropdown') {
                                initialValue = [];
                            } else if (field.inputType === 'selectMultipleDropdown') {
                                initialValue = [];
                            }

                            // Allow config-driven defaults for specific fields
                            // (e.g. Org Admin-only "Auto-create informants?" default selection).
                            if (field.defaultValue !== undefined) {
                                try {
                                    initialValue = JSON.parse(JSON.stringify(field.defaultValue));
                                } catch (_) {
                                    // If cloning fails, fall back to the value as-is (best-effort).
                                    initialValue = field.defaultValue;
                                }
                            }

                            // Initialize regular fields
                            initialData[field.varName] = initialValue;

                            // For custom fields, initialize in the customFields object too
                            if (field.isCustomField) {
                                initialData.customFields[field.varName] = initialValue;
                            }
                        }
                    });
                }
            });
        }

        setFormData(initialData);
    }, [isOpen, sessionOrgConfig]);

    // Get supportClinicianOptions by filtering out the lead clinician 
    const getSupportClinicianOptions = useCallback(() => {
        if (!formData.leadClinician) return clinicianOptions;

        // Handle lead clinician as array or single object
        const leadClinicianValue = Array.isArray(formData.leadClinician)
            ? (formData.leadClinician[0]?.value || '')
            : (formData.leadClinician?.value || '');

        return clinicianOptions.filter(option => option.value !== leadClinicianValue);
    }, [formData.leadClinician, clinicianOptions]);

    // Fetch clinicians for dropdown and set default lead clinician
    useEffect(() => {
        const fetchUsersForDropdowns = async () => {
            if (!isOpen || !userData?.organisation) return;

            try {
                const currentUserUid = auth.currentUser?.uid;
                if (!currentUserUid) return;

                // Query for clinicians (Org. Admin and Clinician user types)
                const cliniciansQuery = query(
                    collection(db, 'clinicians'),
                    where('organisation', '==', userData.organisation),
                    where('userType', 'in', ['Org. Admin', 'Clinician'])
                );

                // Execute both queries
                const [cliniciansSnapshot] = await Promise.all([
                    getDocs(cliniciansQuery),
                ]);

                // Process clinician options.
                //
                // Business rule (non-clinical Org Admins):
                // - We store BOTH clinical and non-clinical Org Admins with userType = 'Org. Admin'
                //   to avoid rewriting rules/permissions.
                // - We use a separate boolean flag isAssignableClinician to decide whether an account
                //   should be selectable as a clinician for patient assignments.
                // - Backwards compatibility: missing isAssignableClinician is treated as "allowed".
                const clinicianOpts = cliniciansSnapshot.docs
                    .filter(docSnap => {
                        const data = docSnap.data() || {};
                        if (data.accountDisabled === true) return false;
                        if (data.isAssignableClinician === false) return false;
                        // Filter clinicians whose pathway specialisms don't match
                        // the selected assessment module for this patient
                        if (!canClinicianBeAssignedToPathway(data, selectedModule)) return false;
                        return true;
                    })
                    .map(docSnap => {
                        const data = docSnap.data() || {};
                        return {
                            value: docSnap.id,
                            label: `${data.title} ${data.firstName} ${data.lastName}`
                        };
                    });
                setClinicianOptions(clinicianOpts);

            } catch (error) {
                console.error("Error fetching users:", error);
            }
        };

        fetchUsersForDropdowns();
    }, [isOpen, userData, selectedModule]);

    // Get all assigned clinicians (lead + supporting)
    const getAllAssignedClinicianOptions = useCallback(() => {
        const assignedOptions = [];
        
        // Add lead clinician if selected
        if (formData.leadClinician) {
            const lead = Array.isArray(formData.leadClinician)
                ? formData.leadClinician[0]
                : formData.leadClinician;
                
            if (lead) {
                assignedOptions.push(lead);
                console.log("Adding lead clinician to assigned options:", lead.label);
            }
        }
        
        // Add support clinicians if any are selected
        if (Array.isArray(formData.supportClinicians) && formData.supportClinicians.length > 0) {
            formData.supportClinicians.forEach(clinician => {
                assignedOptions.push(clinician);
                console.log("Adding support clinician to assigned options:", clinician.label);
            });
        }
        
        console.log(`Total assigned clinicians: ${assignedOptions.length}`, 
            assignedOptions.map(c => c.label));
            
        return assignedOptions;
    }, [formData.leadClinician, formData.supportClinicians]);

    // Create a function to get the right options based on field name
    const getOptionsForField = useCallback((varName) => {
        console.log(`Getting options for field: ${varName}, current step: ${currentStep}`);
                
        // For special fields, use the appropriate options
        if (varName === 'leadClinician') {
            return clinicianOptions;
        } else if (varName === 'supportClinicians') {
            return getSupportClinicianOptions();
        }

        // For Step 3 clinician assignment fields, only show assigned clinicians
        if (varName.startsWith('clinician') && currentStep === 3) {
            const assignedOptions = getAllAssignedClinicianOptions();
            console.log(`Returning ${assignedOptions.length} assigned clinicians for ${varName}`);
            
            // Only return clinicians that have been assigned in step 2
            if (assignedOptions.length > 0) {
                return assignedOptions;
            } else {
                console.warn("No assigned clinicians found, falling back to all clinicians");
                return clinicianOptions;
            }
        }

        // Default case - return clinician options
        console.log(`Returning all ${clinicianOptions.length} clinicians as default`);
        return clinicianOptions;
    }, [
        clinicianOptions, 
        getSupportClinicianOptions, 
        currentStep, 
        getAllAssignedClinicianOptions,
    ]);

    // Auto-select clinician badges when there's only one option
    useEffect(() => {
        if (currentStep === 3) {
            const allAssignedClinicians = getAllAssignedClinicianOptions();

            // If there's exactly one clinician assigned, auto-select them for all fields
            if (allAssignedClinicians.length === 1) {
                const singleClinician = allAssignedClinicians[0];
                const updates = {};

                Object.values(orgConfig.steps['3'].fields).forEach(field => {
                    if (field.inputType === 'selectMultipleButtons') {
                        updates[field.varName] = [singleClinician];
                    } else {
                        updates[field.varName] = singleClinician;
                    }
                });

                if (Object.keys(updates).length > 0) {
                    setFormData(prev => ({
                        ...prev,
                        ...updates
                    }));
                }
            }
        }
    }, [currentStep, getAllAssignedClinicianOptions, orgConfig]);

    /**
     * Determine if lead clinician is selected (used to enable/disable support dropdown).
     *
     * UX decision: Support clinician dropdown is disabled until Lead is selected.
     * This prevents the "support-selected-first" bug where selecting support before lead
     * causes incorrect auto-assignment of clinicians to assessment stages.
     */
    const isLeadClinicianSelected = useMemo(() => {
        if (!formData.leadClinician) return false;
        if (Array.isArray(formData.leadClinician)) {
            return formData.leadClinician.length > 0 && formData.leadClinician[0]?.value;
        }
        return Boolean(formData.leadClinician?.value);
    }, [formData.leadClinician]);

    // When lead clinician changes, update support clinicians accordingly:
    // 1. If lead is cleared, clear support clinicians (they depend on lead being selected first)
    // 2. If lead is set, remove lead from support clinicians if present (can't be both lead and support)
    useEffect(() => {
        // Check if lead is selected
        const hasLead = formData.leadClinician && (
            Array.isArray(formData.leadClinician)
                ? formData.leadClinician.length > 0 && formData.leadClinician[0]?.value
                : formData.leadClinician?.value
        );

        // If lead is cleared but support clinicians exist, clear them
        // (Support dropdown is disabled until lead is selected, so this handles the edge case
        // where user clears lead after already selecting support)
        if (!hasLead && formData.supportClinicians && formData.supportClinicians.length > 0) {
            setFormData(prev => ({
                ...prev,
                supportClinicians: []
            }));
            return;
        }

        // If lead is set and support clinicians exist, remove lead from support if present
        if (hasLead && formData.supportClinicians) {
            const leadClinicianValue = Array.isArray(formData.leadClinician)
                ? (formData.leadClinician[0]?.value || '')
                : (formData.leadClinician?.value || '');

            const updatedSupportClinicians = formData.supportClinicians.filter(
                clinician => clinician.value !== leadClinicianValue
            );

            // Only update if something changed (avoid infinite loop)
            if (updatedSupportClinicians.length !== formData.supportClinicians.length) {
                setFormData(prev => ({
                    ...prev,
                    supportClinicians: updatedSupportClinicians
                }));
            }
        }
    }, [formData.leadClinician, formData.supportClinicians]);

    // ------------------------------------------------------------
    // Reconcile clinician stage assignments when Lead/Supporting changes
    //
    // Why this exists:
    // - Step 3 ("Assign Clinicians to Assessment Stages") only shows *assigned* clinicians
    //   (Lead + Supporting) as available options.
    // - If the user goes back to Step 2 and changes Lead/Supporting, existing Step 3 selections
    //   can become stale (e.g., clinicianDD still contains the old MDT review clinician).
    // - This stale selection can incorrectly trigger limits/disabled states (e.g. DD max=2),
    //   making new valid clinicians appear greyed out.
    //
    // Design goals:
    // - Keep state consistent with the current "assigned clinician" set
    // - Preserve user choices when still valid
    // - Be performant (small set operations; only update state when something truly changes)
    // - Avoid brittle reliance on step numbers: use config to discover which fields exist and their types
    // ------------------------------------------------------------
    useEffect(() => {
        const lead = Array.isArray(formData.leadClinician)
            ? formData.leadClinician[0]
            : formData.leadClinician;
        const supports = Array.isArray(formData.supportClinicians) ? formData.supportClinicians : [];

        // Build a map from clinicianId -> canonical option object (so labels stay consistent)
        const allowedOptions = [lead, ...supports].filter(Boolean);
        const allowedById = new Map();
        allowedOptions.forEach((opt) => {
            if (opt?.value) allowedById.set(opt.value, opt);
        });

        // Nothing to reconcile if we have no lead/support selections yet or no step config.
        if (!orgConfig?.steps) return;

        // Discover clinician assignment fields across steps (we care about stage fields like clinicianCI/SSI/DD/PAD).
        // We intentionally *exclude* leadClinician/supportClinicians themselves.
        const stageFields = [];
        for (const stepKey of Object.keys(orgConfig.steps || {})) {
            const step = orgConfig.steps[stepKey];
            const fields = step?.fields || {};
            for (const fk of Object.keys(fields)) {
                const f = fields[fk];
                if (!f?.varName) continue;
                if (!f.varName.startsWith('clinician')) continue;
                stageFields.push({ varName: f.varName, inputType: f.inputType });
            }
        }
        if (stageFields.length === 0) return;

        // Helper: compare arrays of select options by value (order-sensitive)
        const sameOptionArray = (a, b) => {
            const arrA = Array.isArray(a) ? a : [];
            const arrB = Array.isArray(b) ? b : [];
            if (arrA.length !== arrB.length) return false;
            for (let i = 0; i < arrA.length; i++) {
                if ((arrA[i]?.value || null) !== (arrB[i]?.value || null)) return false;
            }
            return true;
        };

        setFormData((prev) => {
            const updates = {};

            // Recompute using prev for correctness inside setState callback
            const prevLead = Array.isArray(prev.leadClinician) ? prev.leadClinician[0] : prev.leadClinician;
            const prevSupports = Array.isArray(prev.supportClinicians) ? prev.supportClinicians : [];
            const prevAllowedOptions = [prevLead, ...prevSupports].filter(Boolean);
            const prevAllowedById = new Map();
            prevAllowedOptions.forEach((opt) => {
                if (opt?.value) prevAllowedById.set(opt.value, opt);
            });

            // Normalise each clinician stage field
            stageFields.forEach(({ varName, inputType }) => {
                if (varName === 'clinicianDD' && inputType === 'selectMultipleButtons') {
                    const current = Array.isArray(prev.clinicianDD) ? prev.clinicianDD : [];
                    const filtered = current
                        .filter((c) => prevAllowedById.has(c?.value))
                        .map((c) => prevAllowedById.get(c.value));

                    // Always include the lead clinician if present (and ensure it's first)
                    const next = [];
                    if (prevLead?.value) next.push(prevAllowedById.get(prevLead.value) || prevLead);
                    filtered.forEach((c) => {
                        if (c?.value && (!prevLead?.value || c.value !== prevLead.value)) next.push(c);
                    });

                    // If we only have lead and there's exactly one support, auto-fill with that support.
                    if (next.length < 2 && prevSupports.length === 1 && prevSupports[0]?.value) {
                        const s = prevAllowedById.get(prevSupports[0].value) || prevSupports[0];
                        if (!next.some((x) => x?.value === s?.value)) next.push(s);
                    }

                    // Enforce max=2 (Lead + one MDT review clinician)
                    const nextTrimmed = next.slice(0, 2);
                    if (!sameOptionArray(current, nextTrimmed)) {
                        updates.clinicianDD = nextTrimmed;
                    }
                    return;
                }

                // Single-select clinician fields (CI/SSI/PAD/DD in non-MDT orgs, etc.)
                if (inputType === 'selectOneButtons') {
                    const current = prev[varName];
                    const currentId = current?.value;
                    const isCurrentAllowed = currentId && prevAllowedById.has(currentId);

                    if (isCurrentAllowed) {
                        // Keep selection, but replace with canonical object in case labels changed
                        const canonical = prevAllowedById.get(currentId);
                        if (canonical && canonical !== current) {
                            updates[varName] = canonical;
                        }
                        return;
                    }

                    // Current selection is missing or no longer allowed → choose a safe default.
                    let next = null;

                    if (prevLead?.value) {
                        next = prevAllowedById.get(prevLead.value) || prevLead;
                    } else if (prevSupports.length === 1) {
                        next = prevAllowedById.get(prevSupports[0].value) || prevSupports[0];
                    } else if (prevAllowedOptions.length === 1) {
                        next = prevAllowedOptions[0];
                    }

                    if (next !== current) {
                        updates[varName] = next;
                    }
                }
            });

            if (Object.keys(updates).length === 0) return prev;
            return { ...prev, ...updates };
        });
    }, [formData.leadClinician, formData.supportClinicians, orgConfig]);

    // Note: clinicianDD (and other stage assignments) are reconciled by the
    // "Reconcile clinician stage assignments when Lead/Supporting changes" effect above.

    // validateEmail is imported from ../utils/patientSetupValidation so the
    // wizard and its validation helpers share one definition.

    // Handle input changes for any field
    const handleInputChange = (fieldName, value) => {
        // Skip handling for demoOrTest in production
        if (IS_PRODUCTION && fieldName === 'demoOrTest') {
            return;
        }
        
        // For button selectors, handle special case
        if (typeof value === 'object' && value !== null && 'selected' in value) {
            // For selectOneButtons, deselect others and select the clicked one
            if (value.type === 'selectOneButtons') {
                // Special handling for clinicianDD to always use the lead clinician
                if (fieldName === 'clinicianDD') {
                    const leadClinician = Array.isArray(formData.leadClinician)
                        ? formData.leadClinician[0]
                        : formData.leadClinician;

                    // If trying to deselect the lead clinician, prevent it
                    if (!value.selected && value.clinician.value === leadClinician?.value) {
                        return; // Don't allow deselection of lead
                    }

                    // Otherwise, handle normally
                    setFormData(prev => ({
                        ...prev,
                        [fieldName]: value.selected ? value.clinician : null
                    }));
                    return;
                }

                // Normal handling for other fields
                setFormData(prev => ({
                    ...prev,
                    [fieldName]: value.selected ? value.clinician : null
                }));
                return;
            }
            // For selectMultipleButtons, toggle the selection
            if (value.type === 'selectMultipleButtons') {
                setFormData(prev => {
                    const currentSelections = Array.isArray(prev[fieldName]) ? [...prev[fieldName]] : [];

                    // Special handling for clinicianDD to always include lead clinician
                    if (fieldName === 'clinicianDD') {
                        // If attempting to remove lead clinician, don't allow it
                        if (!value.selected &&
                            value.clinician.value ===
                            (Array.isArray(prev.leadClinician)
                                ? prev.leadClinician[0]?.value
                                : prev.leadClinician?.value)) {
                            return prev; // Keep selections unchanged
                        }

                        // If adding a new selection, ensure lead clinician is still there
                        if (value.selected) {
                            // Check if we're at the limit of 2 clinicians for clinicianDD
                            if (currentSelections.length >= 2 && !currentSelections.some(c => c.value === value.clinician.value)) {
                                // Don't add more clinicians if we already have 2
                                return prev;
                            }
                            
                            // Add to selection if not already included
                            if (!currentSelections.some(c => c.value === value.clinician.value)) {
                                currentSelections.push(value.clinician);
                            }
                        } else {
                            // Remove from selection if not lead clinician
                            const index = currentSelections.findIndex(c => c.value === value.clinician.value);
                            if (index !== -1) {
                                currentSelections.splice(index, 1);
                            }
                        }

                        return {
                            ...prev,
                            [fieldName]: currentSelections
                        };
                    } else {
                        // Standard behaviour for other fields
                        if (value.selected) {
                            // Add to selection if not already included
                            if (!currentSelections.some(c => c.value === value.clinician.value)) {
                                currentSelections.push(value.clinician);
                            }
                        } else {
                            // Remove from selection
                            const index = currentSelections.findIndex(c => c.value === value.clinician.value);
                            if (index !== -1) {
                                currentSelections.splice(index, 1);
                            }
                        }

                        return {
                            ...prev,
                            [fieldName]: currentSelections
                        };
                    }
                });
                return;
            }
        }

        // Find field config to check if it's a custom field
        const isCustomField = (() => {
            // Check all steps for this field
            for (const stepKey in orgConfig.steps) {
                const step = orgConfig.steps[stepKey];
                for (const fieldKey in step.fields) {
                    const field = step.fields[fieldKey];
                    if (field.varName === fieldName && field.isCustomField === true) {
                        return true;
                    }
                }
            }
            return false;
        })();

        // For custom fields, store in the customFields object
        if (isCustomField) {
            setFormData(prev => {
                // Handle dropdown selections (both single and multi)
                let processedValue = value;

                // For React Select single selection (using isMulti styling)
                if (Array.isArray(value) && value.length > 0) {
                    processedValue = value[0].value;
                }
                // For regular React Select single selection
                else if (value && typeof value === 'object' && 'value' in value) {
                    processedValue = value.value;
                }

                const next = applyDependentFieldClears(fieldName, {
                    ...prev,
                    customFields: {
                        ...prev.customFields || {},
                        [fieldName]: processedValue
                    },
                    // Also keep in main object for UI
                    [fieldName]: value
                }, orgConfig);

                // A CYP demo preset names the child's sex. Gender and Sex
                // assigned at birth are asked earlier in the wizard, so a
                // clinician can set Female and then pick a male demo. Copy
                // the preset's identity onto both fields now, in both places
                // the form reads them: the dropdown (an array of one option)
                // and customFields (the plain string that is submitted). The
                // backend pins the same values again.
                if (fieldName === 'demoOrTest') {
                    const presetValue = Array.isArray(value) && value.length > 0
                        ? value[0]?.value
                        : null;
                    const pinnedIdentity = demoIdentityForCypPreset(presetValue);
                    if (pinnedIdentity) {
                        const pinnedCustomFields = { ...(next.customFields || {}) };
                        Object.entries(pinnedIdentity).forEach(([pinnedVar, option]) => {
                            next[pinnedVar] = [option];
                            pinnedCustomFields[pinnedVar] = option.value;
                        });
                        next.customFields = pinnedCustomFields;
                    }
                }

                return next;
            });
        } else {
            // Special handling for clinicianDD (Diagnostic Decision) field if it's a direct assignment
            if (fieldName === 'clinicianDD' && Array.isArray(value)) {
                setFormData(prev => {
                    // Get the lead clinician
                    const leadClinician = Array.isArray(prev.leadClinician)
                        ? prev.leadClinician[0]
                        : prev.leadClinician;

                    // Limit to 2 clinicians maximum
                    let finalValue = value.slice(0, 2);

                    // If lead clinician exists and is not in the new selection, add it
                    if (leadClinician && !finalValue.some(c => c.value === leadClinician.value)) {
                        // If we already have 2 clinicians, replace the last one with the lead
                        if (finalValue.length >= 2) {
                            finalValue = [leadClinician, finalValue[0]];
                        } else {
                            finalValue = [leadClinician, ...finalValue];
                        }
                    }

                    return {
                        ...prev,
                        [fieldName]: finalValue
                    };
                });
            } else {
                // Handle regular fields
                setFormData(prev => ({
                    ...prev,
                    [fieldName]: value
                }));
            }

            // Special case for email validation
            if (fieldName === 'email') {
                const isValid = validateEmail(value);
                if (!isValid && value) {
                    setSubmitError('Please enter a valid email address');
                } else {
                    setSubmitError('');
                }
            }
        }
    };

    // Validate current step
    const isStepValid = () => {
        // Nothing is valid while the module fetch is in-flight
        if (modulesLoading) return false;

        // The module selector step is always valid once a selection is made
        if (hasMultipleModules && currentStep === 1) {
            return Boolean(selectedModule);
        }

        // Make sure orgConfig and steps exist
        if (!orgConfig || !orgConfig.steps) return false;
        
        // Adjust step key to account for the prepended module selector step
        const configKey = (currentStep - configStepOffset).toString();
        const currentStepConfig = orgConfig.steps[configKey];
        if (!currentStepConfig) return false;

        // Delegate the per-field + requiredOneOf group checks to the shared,
        // unit-tested validation helper (single source of truth with the
        // "missing fields" message builder below).
        return getStepValidation(currentStepConfig, formData, { isProduction: IS_PRODUCTION }).isValid;
    };

    // Add a function to check if specific step fields are missing
    const getMissingRequiredFields = () => {
        // Module selector step has no "missing fields" concept
        if (hasMultipleModules && currentStep === 1) return [];

        if (!orgConfig || !orgConfig.steps) return [];
        
        const configKey = (currentStep - configStepOffset).toString();
        const currentStepConfig = orgConfig.steps[configKey];
        if (!currentStepConfig || !currentStepConfig.fields) return [];

        // Shared implementation with isStepValid — returns the human-readable
        // labels for any required field (or requiredOneOf group) not yet satisfied.
        return getStepValidation(currentStepConfig, formData, { isProduction: IS_PRODUCTION }).missingFields;
    };

    const isCypAdhd = selectedModule === 'cypAdhd';

    const handleNext = async () => {
        // Email validation: only on the config step that contains the email field.
        // For CYP ADHD, validate the guardian email instead of the patient email.
        if (isCypAdhd) {
            // Guardian email is on the guardian info step (config step 2, UI step 2 + offset)
            const guardianConfigStep = 2 + configStepOffset;
            if (currentStep === guardianConfigStep && formData.guardianEmail) {
                if (!validateEmail(formData.guardianEmail)) {
                    setSubmitError('Please enter a valid email address for the parent or carer');
                    return;
                }
            }
        } else {
            const emailConfigStep = 1 + configStepOffset;
            if (currentStep === emailConfigStep && formData.email) {
                if (!validateEmail(formData.email)) {
                    setSubmitError('Please enter a valid email address');
                    return;
                }

                // Check if the email already exists
                // const exists = await checkEmailExists(formData.email);
                const exists = false;

                if (exists) {
                    setSubmitError('A patient with this email already exists in your organisation');
                    return;
                }
            }
        }

        // Check for missing required fields
        const missingFields = getMissingRequiredFields();
        if (missingFields.length > 0) {
            setSubmitError(`Please complete the following required fields: ${missingFields.join(', ')}`);
            return;
        }

        // Clear any previous errors when validation passes
        setSubmitError('');

        // If all validation passes, proceed to next step
        if (currentStep < totalSteps) {
            setCurrentStep(prev => prev + 1);
        } else {
            // This is the final step - submit the form
            await handleSubmit();
        }
    };

    // Handle back button click
    const handleBack = () => {
        if (currentStep > 1) {
            // Step-scoped validation messages should not carry over to earlier wizard screens.
            setSubmitError('');
            setCurrentStep(prev => prev - 1);
        }
    };

    // Define the clinician dropdown fields exactly as in the original
    const clinicianDropdownFields = [
        { key: 'clinicianCI', label: 'Clinical Interview' },
        { key: 'clinicianSSI', label: 'Semi-Structured Interview' },
        { key: 'clinicianDD', label: 'Diagnostic Decision' },
        { key: 'clinicianPAD', label: 'Post-Assessment Discussion' }
    ];


    // Submit handler
    const handleSubmit = async () => {
        setIsSubmitting(true);
        setSubmitError('');

        try {
            // Validate email: for CYP ADHD validate the guardian email, otherwise the patient email
            if (isCypAdhd) {
                if (formData.guardianEmail && !validateEmail(formData.guardianEmail)) {
                    setSubmitError('Please enter a valid email address for the parent or carer');
                    setIsSubmitting(false);
                    return;
                }
                // School representative at setup is optional but all-or-nothing:
                // a partial entry silently dropped would leave the clinician
                // believing the school was invited when it was not. Mirrors the
                // backend validation in setupNewPatient.
                const schoolRepValues = {
                    firstName: formData.schoolRepFirstName?.trim(),
                    email: formData.schoolRepEmail?.trim(),
                    role: formData.schoolRepRole?.trim(),
                    schoolName: formData.schoolRepSchoolName?.trim(),
                };
                const anySchoolRepField =
                    Object.values(schoolRepValues).some(Boolean) || formData.schoolRepLastName?.trim();
                if (anySchoolRepField) {
                    const missing = [
                        !schoolRepValues.firstName && 'first name',
                        !schoolRepValues.email && 'email',
                        !schoolRepValues.role && 'role at the school',
                        !schoolRepValues.schoolName && 'school name',
                    ].filter(Boolean);
                    if (missing.length > 0) {
                        setSubmitError(
                            `To invite the school representative at setup, provide their ${missing.join(', ')} — or leave every school field blank to let the parent nominate them.`
                        );
                        setIsSubmitting(false);
                        return;
                    }
                    if (!validateEmail(schoolRepValues.email)) {
                        setSubmitError('Please enter a valid email address for the school representative');
                        setIsSubmitting(false);
                        return;
                    }
                }
            } else if (formData.email && !validateEmail(formData.email)) {
                setSubmitError('Please enter a valid email address');
                setIsSubmitting(false);
                return;
            }

            // Process custom fields to extract values
            const processedCustomFields = {};

            // Adult Autism, TOC Adult ADHD and TOC CYP ADHD ask for a
            // subcontract (ADHD pathways only under National RTC, same ICB
            // list). Configs that omit the field — Lancaster Adult ADHD,
            // WLC CYP, and other Adult ADHD orgs — must not keep a value
            // left over from switching pathway.
            const setupConfigCollectsSubcontract = Object.values(orgConfig?.steps || {}).some(
                (step) => Object.values(step?.fields || {}).some(
                    (field) => field?.varName === 'subContractType'
                )
            );

            // Process each custom field
            if (formData.customFields) {
                Object.keys(formData.customFields).forEach(key => {
                    // Skip demoOrTest field in production
                    if (IS_PRODUCTION && key === 'demoOrTest') {
                        return;
                    }

                    // Skip fields whose enabledWhen condition is not met —
                    // they are disabled in the UI and their stale value (if
                    // any) should not be persisted to Firestore.
                    // Subcontract is stored only when this pathway's setup
                    // config actually asks for it (Adult Autism, TOC Adult
                    // ADHD under National RTC, and TOC CYP ADHD under
                    // National RTC). Configs that omit the field drop a
                    // leftover value from switching pathway.
                    if (key === 'subContractType' && !setupConfigCollectsSubcontract) {
                        return;
                    }

                    const fieldConfig = (() => {
                        for (const step of Object.values(orgConfig?.steps || {})) {
                            for (const f of Object.values(step.fields || {})) {
                                if (f?.varName === key) return f;
                            }
                        }
                        return null;
                    })();
                    if (fieldConfig?.enabledWhen && !isFieldEnabled(fieldConfig, formData)) {
                        return;
                    }

                    let value = formData.customFields[key];

                    // Find field input type
                    const inputType = (() => {
                        for (const stepKey in orgConfig.steps) {
                            const step = orgConfig.steps[stepKey];
                            for (const fieldKey in step.fields) {
                                const field = step.fields[fieldKey];
                                if (field.varName === key) {
                                    return field.inputType;
                                }
                            }
                        }
                        return null;
                    })();

                    // Process dropdown values
                    if (inputType === 'selectOneDropdown') {
                        // For single selects styled as multi
                        if (Array.isArray(value) && value.length > 0) {
                            value = value[0].value;
                        }
                        // Empty array means the user left the dropdown unselected.
                        // Normalize to null so the backend receives a clean falsy value
                        // instead of [], which would cause "unhashable type: 'list'"
                        // errors when checked against Python sets.
                        else if (Array.isArray(value)) {
                            value = null;
                        }
                        // For regular single selects
                        else if (value && typeof value === 'object' && 'value' in value) {
                            value = value.value;
                        }
                    } else if (inputType === 'selectMultipleDropdown') {
                        // For multi-selects, extract the value from each item
                        if (Array.isArray(value)) {
                            value = value.map(item => item.value);
                        }
                    }

                    // Sanitize string values by trimming leading/trailing whitespace.
                    // This prevents issues like reference numbers with accidental spaces
                    // (e.g., " 123" instead of "123") which would cause
                    // Firestore queries to fail when searching by exact match.
                    // Note: We only trim leading/trailing spaces; internal spaces are preserved.
                    if (typeof value === 'string') {
                        value = value.trim();
                    }

                    processedCustomFields[key] = value;
                });
            }

            // Transform assigned clinicians data
            const assignedClinicians = {};

            // First add the lead clinician if selected
            if (formData.leadClinician) {
                const lead = Array.isArray(formData.leadClinician) 
                    ? formData.leadClinician[0] 
                    : formData.leadClinician;
                    
                if (lead) {
                    if (!assignedClinicians[lead.value]) {
                        assignedClinicians[lead.value] = {
                            stages: ['Lead'],
                            name: lead.label
                        };
                    } else {
                        // If clinician already exists in other roles, add Lead role
                        assignedClinicians[lead.value].stages.push('Lead');
                    }
                }
            }

            // Then add other clinician assignments
            clinicianDropdownFields.forEach(field => {
                let clinician = formData[field.key];
                
                // Handle both objects and arrays consistently
                if (Array.isArray(clinician)) {
                    // For fields like clinicianDD that can have multiple selections
                    clinician.forEach(cli => {
                        if (!assignedClinicians[cli.value]) {
                            assignedClinicians[cli.value] = {
                                stages: [],
                                name: cli.label
                            };
                        }
                        assignedClinicians[cli.value].stages.push(field.key.replace('clinician', ''));
                    });
                } else if (clinician) {
                    // For fields with single selection
                    if (!assignedClinicians[clinician.value]) {
                        assignedClinicians[clinician.value] = {
                            stages: [],
                            name: clinician.label
                        };
                    }
                    assignedClinicians[clinician.value].stages.push(field.key.replace('clinician', ''));
                }
            });

            const setupFunction = httpsCallable(functions, 'setupNewPatient');
            
            // Call the appropriate cloud function
            // Org Admin-only operational toggle.
            //
            // Important: this is NOT stored in patient customFields; it is only a request-time instruction
            // to the backend (whether to auto-create informants for demo/training patients).
            // For non-Org Admins, we omit it from the payload entirely to preserve current behavior.
            //
            // For autism patients, the value is a string ("autoCreateOneInformant" / "autoCreateTwoInformants")
            // indicating how many informants to create. For ADHD, it remains a simple boolean (true/false).
            let autoCreateInformantsRequested;
            if (userData?.userType === 'Org. Admin') {
                const selection = formData.autoCreateInformantsRequested;
                const selectedValue =
                    Array.isArray(selection) && selection.length > 0 ? selection[0]?.value : undefined;

                if (selectedModule === 'adultAutism') {
                    // Pass the string value directly so the backend knows 1 vs 2 informants
                    autoCreateInformantsRequested = selectedValue || false;
                } else {
                    // ADHD pathway: legacy boolean behavior
                    autoCreateInformantsRequested = Boolean(selectedValue);
                }
            }

            const payload = {             
                firstName: formData.firstName?.trim(),
                lastName: formData.lastName?.trim(),
                customFields: processedCustomFields,
                assignedClinicians,
                organisation: userData.organisation,
                assessmentModule: selectedModule,
            };

            // CYP ADHD patients don't have their own email — the guardian's
            // details are sent as a nested object so the backend can create
            // the guardian informant alongside the child's patient record.
            if (isCypAdhd) {
                payload.guardian = {
                    firstName: formData.guardianFirstName?.trim(),
                    lastName: formData.guardianLastName?.trim() || '',
                    email: formData.guardianEmail?.trim()?.toLowerCase(),
                    // Relationship-to-child is deliberately NOT asked at setup
                    // (product decision, 21 Aug) — the parent confirms it on
                    // their basic information form. The generic label seeds
                    // the informant record until then.
                    relationship: 'Parent/Carer',
                };
                // The child's pronouns are likewise NOT asked at setup — the
                // clinic won't know them at referral. They are captured on
                // the parent's basic information form.
                const genderVal = processedCustomFields.genderIdentity;
                if (genderVal) payload.gender = genderVal;
                const dobVal = processedCustomFields.dateOfBirth;
                if (dobVal) payload.dateOfBirth = dobVal;
                // Sex assigned at birth is optional at setup. When given it is
                // written to the patient header (the canonical copy that the
                // periods gate reads) and seeded onto the parent's About form
                // for them to confirm; when blank the parent answers fresh.
                const sexAtBirthVal = processedCustomFields.sexAssignedAtBirth;
                if (sexAtBirthVal) payload.sexAssignedAtBirth = sexAtBirthVal;
                // School representative provided at setup (orgs whose CYP
                // config collects it): the backend creates the account and
                // marks the parent's nomination as satisfied. Validated as
                // all-or-nothing above, so presence of the email means the
                // set is complete.
                if (formData.schoolRepEmail?.trim()) {
                    payload.schoolRep = {
                        firstName: formData.schoolRepFirstName?.trim(),
                        lastName: formData.schoolRepLastName?.trim() || '',
                        email: formData.schoolRepEmail?.trim()?.toLowerCase(),
                        role: formData.schoolRepRole?.trim(),
                        schoolName: formData.schoolRepSchoolName?.trim(),
                    };
                }
            } else {
                payload.email = formData.email.toLowerCase();
            }

            if (userData?.userType === 'Org. Admin') {
                payload.autoCreateInformantsRequested = autoCreateInformantsRequested;
            }

            const result = await setupFunction(payload);

            if (result.data.success) {
                // Optimistic (post-success) UI update:
                // Inject a lightweight summary stub into the dashboard table immediately
                // so the new patient row appears without waiting for a Firestore round-trip.
                // This matches the pattern used by archive/remove/restore — the optimistic
                // state is the truth (the CF succeeded), and the next SWR cycle (~30s)
                // will replace the stub with the full server-computed summary.
                //
                // We deliberately skip refreshPatientsData here: a forced getDocs() could
                // return stale data if the patient_summaries write hasn't propagated yet,
                // which would overwrite the optimistic entry and make the patient disappear.
                try {
                    const stub = buildOptimisticPatientSummary({
                        patientId: result.data.patientId,
                        formData,
                        assignedClinicians,
                        processedCustomFields,
                        userData,
                        creatorDisplayName: auth.currentUser?.displayName || '',
                        assessmentModule: selectedModule,
                    });
                    optimisticAddPatient(stub);
                } catch (e) {
                    console.warn('Optimistic patient add failed (non-fatal):', e);
                }

                // Refresh only the clinician doc (e.g. patients map) without touching
                // the patient summaries cache. Using refreshUserDataOnly avoids
                // triggering ensurePatientsWithCache, which could overwrite the
                // optimistic stub we just injected if the Firestore summary trigger
                // hasn't propagated yet.
                try {
                    await refreshUserDataOnly();
                } catch (_) {}
                onClose();
                toast.success(`Patient "${formData.firstName} ${formData.lastName}" created successfully.`);
            } else if (result.data.code === "ALREADY_EXISTS") {
                setSubmitError('The email address provided is already in use. Please use a different email address.');
            } else {
                setSubmitError(result.data.message || 'Failed to create account');
            }
        } catch (error) {
            console.error('Error creating patient account:', error);
            
            // Improve error handling to show specific messages
            if (error.code === 'functions/already-exists') {
                setSubmitError('The email address provided is already in use. Please use a different email address.');
            } else if (error.message) {
                // Extract message from Firebase error structure
                const errorMessage = error.message.includes('Firebase:') 
                    ? error.message.split('Firebase:')[1].trim() 
                    : error.message;
                setSubmitError(errorMessage || 'An unexpected error occurred. Please try again.');
            } else {
                setSubmitError('An unexpected error occurred. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    // Render field based on its type
    const renderField = (field) => {
        if (!field || !field.varName || !field.inputType) {
            console.error("Invalid field configuration:", field);
            return null;
        }

        const { varName, displayText, inputType, placeholder, options, description, required, requiredOneOf } = field;
        // Show * for both hard-required fields and fields that are part of a requiredOneOf group
        const showRequiredMark = required || !!requiredOneOf;

        /**
         * UX guardrail: "Auto-create informants?" should only be actionable when
         * the user has selected a non-blank demo/training patient type.
         *
         * Why:
         * - Auto-creating informants only applies to demo/training patients (not blank patients).
         * - Disabling the control makes the dependency explicit and reduces confusion.
         *
         * Note:
         * - Backend still enforces the business rule (safe-by-default); this is purely UI clarity.
         */
        const patientTypeSelection = formData?.demoOrTest; // stored as array for selectOneDropdown
        const selectedPatientTypeValue = Array.isArray(patientTypeSelection) && patientTypeSelection.length > 0
            ? patientTypeSelection[0]?.value
            : undefined;
        const isDemoTrainingPatientTypeSelected =
            Boolean(selectedPatientTypeValue) && selectedPatientTypeValue !== 'Blank Patient';
        const shouldDisableAutoCreateInformantsField =
            varName === 'autoCreateInformantsRequested' && !isDemoTrainingPatientTypeSelected;
        // Once a sexed CYP demo is chosen, Gender and Sex assigned at birth
        // show that sex and cannot be edited. Changing either here would put
        // the form back out of step with the answers the preset writes.
        // Switch patient type instead.
        const pinnedDemoIdentity = demoIdentityForCypPreset(selectedPatientTypeValue);
        const pinnedDemoGender = pinnedDemoIdentity?.[varName] || null;
        const isGenderPinnedByDemo = Boolean(pinnedDemoGender);

        // Generic config-driven dependency: fields with enabledWhen are
        // disabled and visually greyed out when their parent field's value
        // does not match the required condition.  Uses the same pure helper
        // as the validation module so the two stay in lockstep.
        const isDisabledByEnabledWhen = field.enabledWhen && !isFieldEnabled(field, formData);

        // Special handling for blood pressure - render systolic and diastolic together
        if (varName === 'bloodPressureSystolic') {

            return (
                <div key="bloodPressure" className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Blood Pressure{showRequiredMark && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <div className="flex items-center space-x-2">
                        <div className="flex-1">
                            <input
                                type="number"
                                value={formData.bloodPressureSystolic || ''}
                                onChange={(e) => handleInputChange('bloodPressureSystolic', e.target.value)}
                                placeholder="Systolic"
                                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                            />
                        </div>
                        <span className="text-gray-500 font-medium">/</span>
                        <div className="flex-1">
                            <input
                                type="number"
                                value={formData.bloodPressureDiastolic || ''}
                                onChange={(e) => handleInputChange('bloodPressureDiastolic', e.target.value)}
                                placeholder="Diastolic"
                                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                            />
                        </div>
                        <span className="text-gray-500 text-sm">mmHg</span>
                    </div>
                </div>
            );
        }

        // Skip rendering diastolic separately since it's handled with systolic
        if (varName === 'bloodPressureDiastolic') {
            return null;
        }

        // Choose the right options list based on the field name
        let fieldOptions = options; // Default to provided options

        // Dynamic options: when a field declares optionsByParent, swap the
        // dropdown options based on the current value of the parent field
        // (identified by enabledWhen.field). Falls back to the static
        // `options` when no mapping matches the parent value.
        if (field.optionsByParent && field.enabledWhen) {
            const parentField = field.enabledWhen.field;
            const parentVal = formData[parentField];
            const resolvedParent = Array.isArray(parentVal) && parentVal.length > 0
                ? parentVal[0]?.value
                : (parentVal && typeof parentVal === 'object' && 'value' in parentVal)
                    ? parentVal.value
                    : parentVal;
            if (resolvedParent && field.optionsByParent[resolvedParent]) {
                fieldOptions = field.optionsByParent[resolvedParent];
            }
        }

        // Use our helper function for user fields
        if (varName === 'leadClinician' ||
            varName === 'supportClinicians' ||
            varName.startsWith('clinician')) {
            fieldOptions = getOptionsForField(varName);
        }

        switch (inputType) {
            case 'freeText':
                // Name fields share one character rule with Adult ADHD and Adult
                // Autism patient setup: letters (including accents), spaces,
                // hyphens and apostrophes. Numbers and other symbols are
                // stripped as the clinician types. That covers the child's
                // name, the parent/carer's name, the GP's name, and a school
                // representative's name. Surgery, school and role fields are
                // not names of a person, so a practice called "Station Road
                // Surgery" or a role like "Year 6 teacher" can still contain
                // numbers. Email fields only allow valid email characters.
                const isNameField = (
                    varName === 'firstName' || varName === 'lastName'
                    || varName === 'guardianFirstName' || varName === 'guardianLastName'
                    || varName === 'gpName'
                    || varName === 'schoolRepFirstName' || varName === 'schoolRepLastName'
                );
                const isEmailField = varName === 'email';

                return (
                    <div key={varName} className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {displayText}{showRequiredMark && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <input
                            type="text"
                            value={formData[varName] || ''}
                            onChange={(e) => {
                                let sanitizedValue = e.target.value;
                                // Apply appropriate sanitization based on field type
                                if (isNameField) {
                                    sanitizedValue = sanitizeNameInput(sanitizedValue);
                                } else if (isEmailField) {
                                    sanitizedValue = sanitizeEmailInput(sanitizedValue);
                                }
                                handleInputChange(varName, sanitizedValue);
                            }}
                            placeholder={placeholder}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                        />
                    </div>
                );

            case 'freeTextNumerical':
                // Get the appropriate unit suffix based on the field
                const getUnitSuffix = (fieldName) => {
                    switch (fieldName) {
                        case 'height': return 'cm';
                        case 'weight': return 'kg';
                        case 'pulse': return 'bpm';
                        default: return null;
                    }
                };

                const unitSuffix = getUnitSuffix(varName);

                return (
                    <div key={varName} className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {displayText}{showRequiredMark && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <div className="flex items-center space-x-2">
                            <input
                                type="number"
                                value={formData[varName] || ''}
                                onChange={(e) => handleInputChange(varName, e.target.value)}
                                placeholder={placeholder}
                                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                            />
                            {unitSuffix && (
                                <span className="text-gray-500 text-sm">{unitSuffix}</span>
                            )}
                        </div>
                    </div>
                );

            case 'freeTextArea':
                return (
                    <div key={varName} className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {displayText}{showRequiredMark && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <textarea
                            value={formData[varName] || ''}
                            onChange={(e) => handleInputChange(varName, e.target.value)}
                            placeholder={placeholder}
                            rows={4}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 resize-vertical"
                        />
                    </div>
                );

                case 'date':
                    return (
                        <div key={varName} className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {displayText}{showRequiredMark && <span className="text-red-500 ml-1">*</span>}
                            </label>
                            {description && (
                                <p className="text-sm text-gray-500 mb-2">{description}</p>
                            )}
                            <DateOfBirthPicker
                                value={formData[varName] || ''}
                                onChange={(date) => handleInputChange(varName, date)}
                                placeholder={placeholder || "Select date"}
                                className="w-full"
                                minAgeYears={field.minAgeYears}
                                maxAgeYears={field.maxAgeYears}
                            />
                        </div>
                    );
                    
            case 'selectOneDropdown': {

                // The field should be disabled if either the legacy per-field
                // guard fires (auto-create informants) OR the generic
                // enabledWhen condition is unmet.
                const isDropdownDisabled = shouldDisableAutoCreateInformantsField || isDisabledByEnabledWhen || isGenderPinnedByDemo;

                return (
                    <div
                        key={varName}
                        className={`mb-4 ${isDropdownDisabled ? 'opacity-60' : ''}`}
                    >
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {displayText}{showRequiredMark && !isDisabledByEnabledWhen && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        {description && (
                            <p className="text-sm text-gray-500 mb-2">{description}</p>
                        )}
                        {isGenderPinnedByDemo && varName === 'genderIdentity' && (
                            <p className="text-sm text-gray-500 mb-2">
                                {pinnedDemoGender.value === 'Female'
                                    ? 'Set by the demo patient type. Reports for this child use she/her.'
                                    : 'Set by the demo patient type. Reports for this child use he/him.'}
                            </p>
                        )}
                        {isGenderPinnedByDemo && varName === 'sexAssignedAtBirth' && (
                            <p className="text-sm text-gray-500 mb-2">
                                {pinnedDemoGender.value === 'Female'
                                    ? 'Set by the demo patient type. The periods questions are asked for this child.'
                                    : 'Set by the demo patient type. The periods questions are not asked for this child.'}
                            </p>
                        )}
                        {shouldDisableAutoCreateInformantsField && (
                            <p className="text-xs text-gray-500 mb-2">
                                Select a demo/training patient type to enable this option.
                            </p>
                        )}
                        <Select
                            {...PATIENT_SETUP_SELECT_MENU_PROPS}
                            // Real single-select (not the old isMulti-as-single hack)
                            // so typing in the control filters the open list by
                            // substring — needed for long Contract / Subcontract lists.
                            options={fieldOptions}
                            value={
                                isDisabledByEnabledWhen
                                    ? null
                                    : (Array.isArray(formData[varName])
                                        ? (formData[varName][0] ?? null)
                                        : (formData[varName] ?? null))
                            }
                            onChange={(selected) => {
                                if (isDropdownDisabled) return;
                                // Keep storing a one-item array so the rest of
                                // the wizard (validation, submit, dependent
                                // clears) can keep treating this as a dropdown
                                // selection list.
                                handleInputChange(varName, selected ? [selected] : [], true);
                            }}
                            placeholder={placeholder}
                            closeMenuOnSelect={true}
                            isDisabled={isDropdownDisabled}
                            noOptionsMessage={({ inputValue }) => (
                                inputValue
                                    ? `No matches for "${inputValue}"`
                                    : 'No options'
                            )}
                        />
                    </div>
                );
            }

            case 'selectMultipleDropdown':
                /**
                 * Support Clinicians dropdown dependency on Lead Clinician:
                 *
                 * UX decision: The support clinicians dropdown is disabled until a lead clinician
                 * is selected. This prevents a bug where selecting support before lead causes
                 * incorrect auto-assignment of clinicians to assessment stages (CI, SSI, PAD, DD).
                 *
                 * The issue was that if support is selected first, the reconciliation effect
                 * runs with only support in the "allowed" set, so CI/SSI/PAD get auto-assigned
                 * to support instead of lead. Disabling the dropdown enforces the correct order.
                 */
                const isSupportClinicianField = varName === 'supportClinicians';
                const shouldDisableSupportDropdown = isSupportClinicianField && !isLeadClinicianSelected;

                return (
                    <div
                        key={varName}
                        className={`mb-4 ${shouldDisableSupportDropdown ? 'opacity-60' : ''}`}
                    >
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {displayText}{showRequiredMark && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        {description && (
                            <p className="text-sm text-gray-500 mb-2">{description}</p>
                        )}
                        {shouldDisableSupportDropdown && (
                            <p className="text-xs text-gray-500 mb-2">
                                Please select a Lead Clinician first.
                            </p>
                        )}
                        <Select
                            {...PATIENT_SETUP_SELECT_MENU_PROPS}
                            isMulti
                            options={fieldOptions}
                            value={formData[varName]}
                            onChange={(selected) => {
                                if (shouldDisableSupportDropdown) return;
                                handleInputChange(varName, selected);
                            }}
                            placeholder={placeholder}
                            closeMenuOnSelect={false}
                            isDisabled={shouldDisableSupportDropdown}
                        />
                    </div>
                );

            // For selectOneButtons
            case 'selectOneButtons':               
                // Get the appropriate options based on field
                const fieldOptionsOneBtn = getOptionsForField(varName);

                return (
                    <div key={varName} className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {displayText}{showRequiredMark && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        {description && (
                            <p className="text-sm text-gray-500 mb-2">{description}</p>
                        )}

                        {varName === 'clinicianDD' && (
                            <p className="text-xs italic text-violet-600 mb-2">
                                The Lead Clinician is automatically assigned and cannot be changed.
                            </p>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {fieldOptionsOneBtn.map(clinician => {
                                const isSelected = formData[varName] &&
                                    (formData[varName].value === clinician.value);

                                return (
                                    <button
                                        key={clinician.value}
                                        type="button"
                                        onClick={() => handleInputChange(varName, {
                                            type: 'selectOneButtons',
                                            clinician: clinician,
                                            selected: !isSelected
                                        })}
                                        className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium 
                                            transition-all duration-200 ${isSelected
                                                ? 'bg-violet-100 text-violet-800 border border-violet-300 shadow-sm'
                                                : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                                            }`}
                                    >
                                        {clinician.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                );

            // For selectMultipleButtons
            case 'selectMultipleButtons':
                
                // Get the appropriate options based on field
                const fieldOptionsMultiBtn = getOptionsForField(varName);

                return (
                    <div key={varName} className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {displayText}{showRequiredMark && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        {description && (
                            <p className="text-sm text-gray-500 mb-2">{description}</p>
                        )}

                        {varName === 'clinicianDD' && (
                            <p className="text-xs italic text-violet-600 mb-2">
                                {getMinSelections(field) > 1
                                    ? 'The Lead Clinician is automatically included and cannot be removed. Please select the clinician who will support the MDT review.'
                                    : 'The Lead Clinician is automatically included and cannot be removed. Adding a clinician to support the MDT review is optional at this stage \u2014 you can select one now, or add them later from the patient\u2019s overview (the Diagnostic Decision \u201c+\u201d button or the Reassign Clinicians action).'}
                            </p>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {fieldOptionsMultiBtn.map(clinician => {
                                const isSelected = Array.isArray(formData[varName]) &&
                                    formData[varName].some(c => c.value === clinician.value);
                                
                                // For clinicianDD, check if we're at the limit and this clinician is not already selected
                                const isAtLimit = varName === 'clinicianDD' && 
                                    Array.isArray(formData[varName]) && 
                                    formData[varName].length >= 2 && 
                                    !isSelected;

                                return (
                                    <button
                                        key={clinician.value}
                                        type="button"
                                        onClick={() => handleInputChange(varName, {
                                            type: 'selectMultipleButtons',
                                            clinician: clinician,
                                            selected: !isSelected
                                        })}
                                        disabled={isAtLimit}
                                        className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium 
                                            transition-all duration-200 ${isSelected
                                                ? 'bg-violet-100 text-violet-800 border border-violet-300 shadow-sm'
                                                : isAtLimit
                                                    ? 'bg-gray-50 text-gray-400 border border-gray-100 cursor-not-allowed'
                                                    : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                                            }`}
                                    >
                                        {clinician.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                );

            default:
                return <div key={varName}>Unsupported field type: {inputType}</div>;
        }
    };

    // Render step content with proper null checks
    const renderStepContent = () => {
        // While the module fetch is in-flight, show a neutral loading state.
        // This prevents the form from briefly showing the first config step (patient name)
        // before the fetch resolves and the module selector step is prepended.
        if (modulesLoading) {
            return (
                <div className="flex items-center justify-center py-10 text-gray-400 text-sm">
                    Loading…
                </div>
            );
        }

        // Render the pathway selector step when org has multiple modules
        if (hasMultipleModules && currentStep === 1) {
            return (
                <div>
                    <h3 className="text-lg font-medium mb-4">Pathway Selection</h3>
                    <p className="text-sm text-gray-600 mb-4">
                        Select the assessment pathway for this patient.
                    </p>
                    <div className="space-y-2">
                        {availableModules.map((moduleKey) => (
                            <button
                                key={moduleKey}
                                type="button"
                                onClick={() => {
                                    setSelectedModule(moduleKey);
                                    // Clear pathway-dependent fields to prevent stale values
                                    // from one pathway leaking into another (e.g. autism string
                                    // options being submitted as ADHD boolean auto-create).
                                    setFormData(prev => ({
                                        ...prev,
                                        autoCreateInformantsRequested: undefined,
                                        demoOrTest: undefined,
                                        subContractType: undefined,
                                        customFields: {
                                            ...prev.customFields,
                                            demoOrTest: undefined,
                                            subContractType: undefined,
                                        },
                                    }));
                                }}
                                className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all ${
                                    selectedModule === moduleKey
                                        ? 'border-violet-500 bg-violet-50 text-violet-900'
                                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                                }`}
                            >
                                <span className="font-medium">
                                    {ASSESSMENT_MODULE_LABELS[moduleKey] || moduleKey}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            );
        }

        // Adjust step key to account for the prepended module selector step
        const stepKey = (currentStep - configStepOffset).toString();

        // Safely access the step configuration
        if (!orgConfig || !orgConfig.steps || !orgConfig.steps[stepKey]) {
            return <div>Step configuration not found</div>;
        }

        const stepConfig = orgConfig.steps[stepKey];

        return (
            <div>
                <h3 className="text-lg font-medium mb-4">{stepConfig.title || `Step ${currentStep}`}</h3>

                <div className="space-y-4">
                    {/* Safely iterate through fields with null checks */}
                    {stepConfig.fields && Object.keys(stepConfig.fields)
                        .filter((fieldKey) => {
                            const field = stepConfig.fields[fieldKey];
                            // Filter out diastolic field since it's rendered with systolic
                            return field && field.varName !== 'bloodPressureDiastolic';
                        })
                        .map((fieldKey, index) => {
                            const field = stepConfig.fields[fieldKey];
                            if (!field) return null;

                            // Return field with divider (except for the first field)
                            return (
                                <div key={fieldKey}>
                                    {index > 0 && (
                                        <div className="h-px bg-gray-200 my-2" aria-hidden="true" />
                                    )}
                                    {renderField(field)}
                                </div>
                            );
                        })}
                </div>
            </div>
        );
    };

    return (
        <Dialog.Root
          open={isOpen}
          // Accessibility/UX: only close when Radix reports a closing transition.
          // This prevents accidental closes (and loss of typed input) during unrelated re-renders.
          onOpenChange={(open) => {
            // UX guardrail: prevent closing while submit is in-flight, so the user doesn't lose context.
            if (!open && !isSubmitting) onClose();
          }}
        >
        <Dialog.Portal>
            <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" />
            <Dialog.Content
                className="radix-dialog-content-centered-motion fixed z-50 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg max-w-md w-full max-h-[95vh] flex flex-col overflow-hidden"
                onInteractOutside={(e) => e.preventDefault()}
                onPointerDownOutside={(e) => e.preventDefault()}
            >
                <Dialog.Title className="text-xl font-semibold mb-4 px-6 pt-6 flex-shrink-0">
                    Set Up New Patient
                </Dialog.Title>
                <Dialog.Description className="text-sm text-gray-500 mb-4 px-6 flex-shrink-0">
                    Please fill out the following fields to set up a new patient.
                </Dialog.Description>

                <div className="mb-4 px-6 flex-shrink-0">
                        <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-500">
                                Step {currentStep} of {totalSteps}
                            </div>
                            <div className="flex h-2 rounded-full bg-gray-200 w-32">
                                <div
                                    className="h-full bg-violet-500 rounded-full transition-all duration-300 ease-in-out"
                                    style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Scrollable step region; footer stays pinned below */}
                    <div className="flex-1 min-h-0 overflow-y-auto">
                        <div
                            className="overflow-hidden transition-[height] duration-300 ease-in-out"
                            style={{ height: animatedHeight }}
                        >
                            <div ref={contentRef} className="px-6 pt-2 pb-4">
                                {renderStepContent()}

                                {submitError && (
                                    <div className="p-3 mt-3 text-sm font-semibold text-red-600 bg-red-50 rounded-md">
                                        {submitError}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="px-6 pt-4 pb-6 flex justify-between border-t border-gray-100 bg-white flex-shrink-0">
                        {currentStep > 1 ? (
                            <button
                                type="button"
                                onClick={handleBack}
                                disabled={isSubmitting}
                                className="px-4 py-2 text-sm font-medium rounded-md transition-colors bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <div className="flex items-center">
                                    <ChevronLeftIcon className="h-4 w-4 mr-1" />
                                    Back
                                </div>
                            </button>
                        ) : (
                            <Dialog.Close asChild>
                                <button
                                    type="button"
                                    disabled={isSubmitting}
                                    className="px-4 py-2 text-sm font-medium rounded-md transition-colors bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Cancel
                                </button>
                            </Dialog.Close>
                        )}

                        {currentStep === totalSteps ? (
                            <button
                                type="button"
                                onClick={handleNext}
                                disabled={!isStepValid() || isSubmitting}
                                className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors
                                    ${!isStepValid() || isSubmitting
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-violet-600 hover:bg-violet-700'}`}
                            >
                                {isSubmitting ? (
                                    <div className="flex items-center">
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Processing...
                                    </div>
                                ) : 'Submit'}
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={handleNext}
                                disabled={!isStepValid() || isSubmitting}
                                className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors
                                    ${!isStepValid() || isSubmitting
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-violet-600 hover:bg-violet-700'}`}
                            >
                                <div className="flex items-center">
                                    Next
                                    <ChevronRightIcon className="h-4 w-4 ml-1" />
                                </div>
                            </button>
                        )}
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}

export default PatientSetupForm;